import { lazy, cloneElement } from 'react'
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import AuthRoute, { AuthenticatedShell } from 'containers/AuthRoute'
import AuthContainer from 'containers/AuthContainer'
import NotFound from 'pages/common/NotFound'
import Users from 'pages/users/Users'
import Companies from 'pages/company/Companies'
import Cars from 'pages/car/Cars'
import AddEditUser from 'pages/users/AddEditUser'
import AddEditCompany from 'pages/company/AddEditCompany'
import AddEditCar from 'pages/car/AddEditCar'
import Roles from 'pages/roles/Roles'
import Reports from 'pages/reports/Reports'
import Branches from 'pages/branches/branches'
import AddEditBranch from 'pages/branches/AddEditBranch'

import ForgotPassword from 'pages/auth/ForgotPassword'
import Discounts from 'pages/discount/Discounts'
import BlackList from 'pages/blackList/BlackList'
import Imports from 'pages/imports/Imports'
import SalesReports from 'pages/salesReports/reports'
import CreateBookingPage from 'pages/booking/create/CreateBookingPage'
import DashboardStats from 'pages/statsDashboard/stats'

import { filter, flatMap, get, includes, map, some } from 'lodash'
import ViewBooking from 'pages/booking/viewBooking'
import { useSelector } from 'react-redux'
import { getDashboardAccessPaths, getRootRedirectPath, hasAdminRole, isCompanyDashboardUser, isPartnerDashboardUser } from 'lib/auth/dashboardRole'
import BranchDiscount from 'pages/branches/branchDiscount'
import MasterData from 'pages/branches/masterData/MasterData'
import RefundPolicyContentForm from 'pages/branches/masterData/RefundPolicyContentForm'
import AddEditAppBanner from 'pages/branches/masterData/AddEditAppBanner'
import AddEditBooking from 'pages/booking/AddEditBooking'
import Partners from 'pages/partner/partners'
import AddEditPartner from 'pages/partner/AddEditPartner'
import PartnerSettingsStandalonePage from 'pages/partner/PartnerSettingsStandalonePage'
import CompanyDashboard from 'pages/companyDashboard'
import OfficeFinancialReport from 'pages/officeFinancialReport'
import OfficeSalesReport from 'pages/officeSalesReport'
import OfficeEvaluationReports from 'pages/officeEvaluationReports'
import CompanyEcommerceStandalonePage from 'pages/company/CompanySettingsStandalonePage'
import WalletPayoutRequest from 'pages/walletPayoutRequest/payoutRequest'
// Refund engine moved to Master Data → Refund Engine
// import RefundPolicy from 'pages/policy/refundPolicy'
import BookingInvoices from 'pages/invoice/bookingInvoices'
import AddEditBookingInvoice from 'pages/invoice/AddEditBookingInvoice'
import BlogListing from 'pages/blog/BlogListing'
import AddEditBlog from 'pages/blog/AddEditBlog'
import MyProfile from 'pages/account/MyProfile'
import Leads from 'pages/crm/Leads'
import LeadProfile from 'pages/crm/LeadProfile'
import PreBookings from 'pages/preBookings/PreBookings'
import PreBookingDetail from 'pages/preBookings/PreBookingDetail'
import PreBookingsFunnel from 'pages/preBookings/PreBookingsFunnel'
import AddEditLead from 'pages/crm/AddEditLead'
import ShortLinks from 'pages/shortLinks/ShortLinks'
import NewsletterSubscriptions from 'pages/newsletter/NewsletterSubscriptions'
import SortingEngine from 'pages/sortingEngine/SortingEngine'
import Radar from 'pages/radar/Radar'
import GoogleAnalytics from 'pages/googleAnalytics'
import BookingFunnel from 'pages/bookingFunnel'
const DashboardPage = lazy(() => import('pages/dashboard'))
const LoginPage = lazy(() => import('pages/auth/Login'))
const CompanySignupPage = lazy(() => import('pages/auth/CompanySignup'))

const LayoutRouter = ({ loading, isUser, user = {}, openChangePasswordModal }) => {
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const currentUser = userFromStore || user
  const hasAdminAccess = hasAdminRole(currentUser)
  const isPartnerOnly = isPartnerDashboardUser(currentUser)

  const routes = [
    { path: '/login', exact: true, isPublic: true, isUser, component: LoginPage, showSideBar: false },
    { path: '/forgot', exact: true, isPublic: true, isUser, component: <ForgotPassword />, showSideBar: false },
    { path: '/profile', isUser, component: <MyProfile /> },
    { path: '/partner/settings', isUser, component: <PartnerSettingsStandalonePage /> },
    { path: '/dashboard', isUser, component: <DashboardPage /> },
    { path: '/stats-dashboard', isUser, component: <DashboardStats /> },
    { path: '/company-dashboard', isUser, component: <CompanyDashboard /> },
    { path: '/office-financial-report', isUser, component: <OfficeFinancialReport /> },
    { path: '/office-sales-report', isUser, component: <OfficeSalesReport /> },
    { path: '/office-evaluation-reports', isUser, component: <OfficeEvaluationReports /> },
    { path: '/company/ecommerce', isUser, component: <CompanyEcommerceStandalonePage /> },
    { path: '/company/branding', isUser, component: <Redirect to="/company/ecommerce?section=branding" /> },
    { path: '/company/dns', isUser, component: <Redirect to="/company/ecommerce?section=dns" /> },

    { path: '/users/add', isUser, component: <AddEditUser /> },
    { path: '/users/edit/:id', isUser, component: <AddEditUser /> },
    { path: '/transactions', isUser, component: <Reports /> },
    { path: '/bookings', isUser, component: <Reports /> },
    { path: '/audits', isUser, component: <Reports /> },
    // { path: '/users/edit/:id/admins', isUser, component: <Users />, key: 'id' },
    { path: '/branches', isUser, component: <Branches /> },
    { path: '/add/branch', isUser, component: <AddEditBranch /> },
    { path: '/branch/:id', isUser, component: <AddEditBranch /> },
    { path: '/users/:id', isUser, component: <Users /> },
    { path: '/users', isUser, component: <Users /> },
    { path: '/wallet-payout-requests', isUser, component: <WalletPayoutRequest /> },
    { path: '/companies', isUser, component: <Companies /> },
    { path: '/roles', isUser, component: <Roles /> },
    { path: '/add/company', isUser, component: <AddEditCompany /> },
    { path: '/company/edit/:id', isUser, component: <AddEditCompany /> },
    { path: '/cars', isUser, component: <Cars /> },
    { path: '/car/edit/:id', isUser, component: <AddEditCar /> },
    { path: '/discounts', isUser, component: <Discounts /> },
    { path: '/fraud-users', isUser, component: <BlackList /> },
    { path: '/partners/add', isUser, component: <AddEditPartner /> },
    { path: '/partners/edit/:id', isUser, component: <AddEditPartner /> },
    { path: '/partners', isUser, component: <Partners /> },
    { path: '/imports', isUser, component: <Imports /> },
    { path: '/booking/edit/:id', isUser, component: <AddEditBooking /> },
    { path: '/booking/:id', isUser, component: <ViewBooking /> },
    { path: '/salesReport', isUser, component: <SalesReports /> },
    { path: '/create/booking', isUser, component: <CreateBookingPage /> },
    { path: '/branch-discounts', isUser, component: <BranchDiscount /> },
    { path: '/master-data/refund-policy-content/add', isUser, component: <RefundPolicyContentForm /> },
    { path: '/master-data/refund-policy-content/edit/:id', isUser, component: <RefundPolicyContentForm /> },
    { path: '/master-data/app-banner/add', isUser, component: <AddEditAppBanner /> },
    { path: '/master-data/app-banner/edit/:id', isUser, component: <AddEditAppBanner /> },
    { path: '/master-data', exact: true, isUser, component: <MasterData /> },
    // { path: '/refund-policy', isUser, component: <RefundPolicy /> },
    { path: '/booking-invoices', isUser, component: <BookingInvoices /> },
    { path: '/booking-invoice/:id', isUser, component: <AddEditBookingInvoice /> },
    { path: '/blogs/edit/:id/blog-profile', isUser, component: <AddEditBlog /> },
    { path: '/blogs/edit/:id', isUser, component: <AddEditBlog /> },
    { path: '/add/blogs', isUser, component: <AddEditBlog /> },
    { path: '/blogs', isUser, component: <BlogListing /> },
    { path: '/crm/leads/add', isUser, component: <AddEditLead /> },
    { path: '/crm/leads/edit/:id', isUser, component: <AddEditLead /> },
    { path: '/crm/leads/:id', isUser, component: <LeadProfile /> },
    { path: '/crm/leads', isUser, component: <Leads /> },
    { path: '/pre-bookings/funnel', isUser, component: <PreBookingsFunnel /> },
    { path: '/pre-bookings/:id', isUser, component: <PreBookingDetail /> },
    { path: '/pre-bookings', isUser, component: <PreBookings /> },
    { path: '/newsletter-subscriptions', isUser, component: <NewsletterSubscriptions /> },
    { path: '/short-links', isUser, component: <ShortLinks /> },
    { path: '/sorting-engine', isUser, component: <SortingEngine /> },
    { path: '/radar', isUser, component: <Radar /> },
    { path: '/google-analytics', isUser, component: <GoogleAnalytics /> },
    { path: '/booking-funnel', isUser, component: <BookingFunnel /> },
  ]

  const access = getDashboardAccessPaths(currentUser)
  const hasAccess = route => {
    // Keep core routes available while access matrix is still loading/empty.
    if (hasAdminAccess && (!access || access.length === 0)) {
      return true
    }
    if (route.path === '/profile') {
      return true
    }
    if (route.path === '/partner/settings') {
      return isPartnerOnly
    }
    // Company dashboard is the default landing page for company users.
    // Some roles do not explicitly include /company-dashboard in access menus.
    if (route.path === '/company-dashboard') {
      return isCompanyDashboardUser(currentUser)
    }
    if (route.path === '/office-financial-report') {
      return isCompanyDashboardUser(currentUser)
    }
    if (route.path === '/office-sales-report') {
      return isCompanyDashboardUser(currentUser)
    }
    if (route.path === '/office-evaluation-reports') {
      if (!hasAdminAccess) return false
      if (hasAdminAccess && (!access || access.length === 0)) return true
      return includes(access, '/office-evaluation-reports')
    }
    if (route.path === '/short-links') {
      if (!hasAdminAccess) return false
      if (hasAdminAccess && (!access || access.length === 0)) return true
      return includes(access, '/short-links')
    }
    if (route.path === '/sorting-engine') {
      if (!hasAdminAccess) return false
      if (hasAdminAccess && (!access || access.length === 0)) return true
      return includes(access, '/sorting-engine')
    }
    if (route.path === '/radar') {
      if (!hasAdminAccess) return false
      if (hasAdminAccess && (!access || access.length === 0)) return true
      return includes(access, '/radar')
    }
    if (includes(['/users/:id', '/users/edit/:id', '/users'], route.path) && includes(access, '/users')) {
      return true
    }

    if (route.path === '/companies' && includes(access, '/companies')) {
      return true
    }
    if (includes(['/add/company', '/company/edit/:id'], route.path)) {
      if (!hasAdminAccess) return false
      return includes(access, '/companies')
    }
    if (includes(['/salesReport'], route.path) && includes(access, '/salesReport')) {
      return true
    }

    if (includes(['/branch-discounts'], route.path) && includes(access, '/branch-discounts')) {
      return true
    }
    if (
      includes(
        [
          '/master-data',
          '/master-data/refund-policy-content/add',
          '/master-data/refund-policy-content/edit/:id',
          '/master-data/app-banner/add',
          '/master-data/app-banner/edit/:id',
        ],
        route.path
      ) &&
      includes(access, '/master-data')
    ) {
      return true
    }
    if (includes(['/car/edit/:id', '/cars'], route.path) && includes(access, '/cars')) {
      return true
    }
    if (includes(['/bookings', '/booking/:id', '/booking/cancel/:id', '/create/booking', '/booking/edit/:id'], route.path)) {
      if (isPartnerOnly) {
        return includes(['/bookings', '/booking/:id'], route.path)
      }
      if (includes(access, '/bookings')) return true
      if (route.path === '/create/booking' && some(currentUser?.roles, { type: 'company' }) && !hasAdminAccess) return true
      return false
    }
    if (!hasAdminAccess && !currentUser?.superCompanyUser) {
      if (includes(['/fraud-user/:id', '/fraud-users'], route.path) && includes(access, '/fraud-users')) {
        return false
      }
    }
    if (includes(['/partners/add', '/partners/edit/:id', '/partners'], route.path) && includes(access, '/partners')) {
      return true
    }
    if (includes(['/booking-invoice/:id'], route.path) && includes(access, '/booking-invoices')) {
      return true
    }
    if (includes(['/add/branch', '/branch/:id', '/branches'], route.path) && includes(access, '/branches')) {
      if (route.path === '/add/branch' && !hasAdminAccess && !currentUser?.superCompanyUser) {
        return false
      }
      return true
    }
    if (includes(['/add/blogs', '/blogs/edit/:id', '/blogs/edit/:id/blog-profile', '/blogs'], route.path) && includes(access, '/blogs')) {
      return true
    }
    if (includes(['/pre-bookings', '/pre-bookings/:id', '/pre-bookings/funnel'], route.path) && includes(access, '/pre-bookings')) {
      return true
    }

    if (includes(['/crm/leads', '/crm/leads/:id', '/crm/leads/add', '/crm/leads/edit/:id'], route.path) && includes(access, '/crm/leads')) {
      return true
    }
    if (includes(['/company/ecommerce', '/company/branding', '/company/dns'], route.path)) {
      if (isPartnerOnly) return false
      const isCompanyRole = isCompanyDashboardUser(currentUser)
      if (!isCompanyRole) return false
      const companyId = currentUser?.company?._id || currentUser?.company
      if (!companyId) return false
      if (route.path === '/company/branding') return Boolean(currentUser?.canManageBranding)
      if (route.path === '/company/dns') return Boolean(currentUser?.canManageDns)
      if (route.path === '/company/ecommerce') return Boolean(currentUser?.canManageBranding || currentUser?.canManageDns)
    }
    return includes(access, route.path)
  }

  return (
    <Router>
      <AuthContainer loading={loading}>
        {!loading && currentUser ? (
          <>
            <Switch>
              <AuthRoute isUser={isUser} component={<LoginPage />} showSideBar={false} isPublic exact path="/login" openChangePasswordModal={openChangePasswordModal} />
              <AuthRoute isUser={isUser} component={<ForgotPassword />} showSideBar={false} isPublic exact path="/forgot" openChangePasswordModal={openChangePasswordModal} />
              <AuthRoute isUser={isUser} component={<CompanySignupPage />} showSideBar={false} isPublic exact path="/signup" openChangePasswordModal={openChangePasswordModal} />

              <Route
                render={() => {
                  if (!isUser) {
                    return <Redirect to="/login" />
                  }
                  return (
                    <AuthenticatedShell openChangePasswordModal={openChangePasswordModal}>
                      <Switch>
                        {filter(routes, route => hasAccess(route))?.map((route, index) => (
                          <Route
                            key={route.key || `${route.path}-${index}`}
                            path={route.path}
                            exact={route.exact}
                            render={() => cloneElement(route.component, { readOnly: route.readOnly })}
                          />
                        ))}
                        <Route exact path="/" render={() => <Redirect to={getRootRedirectPath(currentUser)} />} />
                        <Route component={NotFound} />
                      </Switch>
                    </AuthenticatedShell>
                  )
                }}
              />
            </Switch>
          </>
        ) : (
          <Switch>
            <AuthRoute isUser={isUser} component={<LoginPage />} showSideBar={false} isPublic exact path="/login" />
            <AuthRoute isUser={isUser} component={<CompanySignupPage />} showSideBar={false} isPublic exact path="/signup" />
            <AuthRoute component={<Redirect to="/login" />} isUser={isUser} path="/" />
            <AuthRoute isUser={isUser} component={<ForgotPassword />} showSideBar={false} isPublic exact path="/forgot" />
          </Switch>
        )}
      </AuthContainer>
    </Router>
  )
}

export default LayoutRouter
